import { useState, useEffect, useRef } from "react";
import {
  Plus,
  Package,
  GripVertical,
  Edit2,
  Trash2,
  MoreVertical,
  MapPin,
  Radio,
  AlertTriangle,
  CheckCircle,
  Signal,
  X,
  ChevronDown,
  ChevronRight,
  Layers,
  Calendar,
  Copy,
  Building,
  CheckSquare,
  Square,
  ArrowRight,
  Zap,
  Server,
  Loader2,
  Wifi,
  WifiOff,
  RefreshCw,
  Activity,
  XCircle,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

const getAuthHeaders = () => ({
  headers: { Authorization: `Bearer ${localStorage.getItem("token")}` }
});

/**
 * SystemSlot - Individual slot in a rack showing system info
 * Now supports dual receivers (2 systems per slot)
 */
function SystemSlot({ 
  slotNumber, 
  systems = [], // Array of 0, 1 or 2 systems
  onDrop, 
  onRemove, 
  isDragging,
  onDragStart,
  onDragEnd 
}) {
  const [isOver, setIsOver] = useState(false);
  const [overPosition, setOverPosition] = useState(null); // 'A' or 'B'
  const slotRef = useRef(null);

  // Get status color based on RF level
  const getStatusColor = (system) => {
    if (!system) return "bg-[#27272A]";
    const rfLevel = system.rf_level || 85;
    if (rfLevel < 50) return "bg-[#FF3B30]/20 border-[#FF3B30]/50";
    if (rfLevel < 70) return "bg-[#FFCC00]/20 border-[#FFCC00]/50";
    return "bg-[#34C759]/10 border-[#34C759]/30";
  };

  const getStatusIndicator = (system) => {
    if (!system) return null;
    const rfLevel = system.rf_level || 85;
    if (rfLevel < 50) return <AlertTriangle className="w-3 h-3 text-[#FF3B30] animate-pulse" />;
    if (rfLevel < 70) return <Signal className="w-3 h-3 text-[#FFCC00]" />;
    return <CheckCircle className="w-3 h-3 text-[#34C759]" />;
  };

  const handleDragOver = (e, position) => {
    e.preventDefault();
    setIsOver(true);
    setOverPosition(position);
  };

  const handleDragLeave = () => {
    setIsOver(false);
    setOverPosition(null);
  };

  const handleDrop = (e, position) => {
    e.preventDefault();
    setIsOver(false);
    setOverPosition(null);
    const systemId = e.dataTransfer.getData("systemId");
    const sourceRackId = e.dataTransfer.getData("sourceRackId");
    if (systemId) {
      // Pass slot number with position suffix (e.g., "1A" or "1B")
      onDrop(systemId, `${slotNumber}${position}`, sourceRackId);
    }
  };

  const handleDragStart = (e, system, position) => {
    if (system) {
      e.dataTransfer.setData("systemId", system.id);
      e.dataTransfer.setData("sourceSlot", `${slotNumber}${position}`);
      onDragStart?.(system.id);
    }
  };

  const systemA = systems[0] || null;
  const systemB = systems[1] || null;

  const renderSystemCell = (system, position) => {
    const isEmpty = !system;
    const isThisOver = isOver && overPosition === position;

    return (
      <div
        className={`flex-1 flex items-center gap-1 p-1.5 rounded border transition-all min-w-0 ${
          isThisOver 
            ? "border-[#007AFF] bg-[#007AFF]/10" 
            : system 
              ? getStatusColor(system)
              : "border-white/[0.05] bg-[#0A0A0A]/50 hover:border-white/[0.1]"
        } ${isDragging ? "opacity-50" : ""}`}
        onDragOver={(e) => handleDragOver(e, position)}
        onDragLeave={handleDragLeave}
        onDrop={(e) => handleDrop(e, position)}
        data-testid={`rack-slot-${slotNumber}${position}`}
      >
        {system ? (
          <div
            draggable
            onDragStart={(e) => handleDragStart(e, system, position)}
            onDragEnd={() => onDragEnd?.()}
            className="flex-1 flex items-center gap-1 cursor-grab active:cursor-grabbing min-w-0"
          >
            <GripVertical className="w-3 h-3 text-[#52525B] flex-shrink-0" />
            
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-1">
                <span className="text-xs font-medium text-white truncate">
                  {system.name || `CH ${system.channel}`}
                </span>
                {getStatusIndicator(system)}
              </div>
              <span className="font-mono text-[10px] text-[#34C759]">
                {parseFloat(system.frequency).toFixed(3)}
              </span>
            </div>

            <Button
              onClick={() => onRemove(system.id)}
              variant="ghost"
              size="sm"
              className="h-5 w-5 p-0 text-[#52525B] hover:text-[#FF3B30] flex-shrink-0"
            >
              <X className="w-3 h-3" />
            </Button>
          </div>
        ) : (
          <div className="flex-1 flex items-center justify-center py-1 text-[10px] text-[#52525B]">
            <span>{position}</span>
          </div>
        )}
      </div>
    );
  };

  return (
    <div
      ref={slotRef}
      className="flex items-center gap-2 p-1.5 rounded bg-[#0A0A0A] border border-white/[0.05]"
      data-testid={`rack-slot-${slotNumber}`}
    >
      {/* Slot number (1U) */}
      <div className="w-6 h-10 flex items-center justify-center text-xs font-mono text-[#52525B] bg-[#18181B] rounded border border-white/[0.05]">
        {slotNumber}
      </div>

      {/* Dual receiver slots A and B */}
      <div className="flex-1 flex gap-1">
        {renderSystemCell(systemA, 'A')}
        {renderSystemCell(systemB, 'B')}
      </div>
    </div>
  );
}

/**
 * RackCard - A single equipment rack display
 */
function RackCard({ 
  rack, 
  systems, 
  onUpdate, 
  onDelete, 
  onAssignSystem, 
  onUnassignSystem,
  draggingSystemId,
  onDragStart,
  onDragEnd,
  onPushToNetwork,
  onAnalyzeCoordination,
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState(rack.name);

  // Map systems to slots - supports dual format (1A, 1B, 2A, 2B, etc.)
  const getSystemsForSlot = (slotNumber) => {
    const systemA = rack.slots?.[`${slotNumber}A`] || rack.slots?.[slotNumber.toString()];
    const systemB = rack.slots?.[`${slotNumber}B`];
    
    return [
      systemA ? systems.find(s => s.id === systemA) : null,
      systemB ? systems.find(s => s.id === systemB) : null
    ].filter(s => s !== undefined); // Keep nulls but remove undefined
  };

  // Count filled slots (each slot can have 2 systems)
  const filledSlots = Object.values(rack.slots || {}).filter(Boolean).length;
  const maxCapacity = rack.slots_count * 2; // Dual receivers

  const handleSaveName = async () => {
    if (editName.trim() && editName !== rack.name) {
      await onUpdate(rack.id, { name: editName });
    }
    setIsEditing(false);
  };

  const handleDrop = (systemId, slotPosition, sourceRackId) => {
    // slotPosition can be "1A", "1B", "2A", etc.
    onAssignSystem(rack.id, systemId, slotPosition);
  };

  return (
    <div 
      className="bg-[#111111] border border-white/[0.08] rounded-sm overflow-hidden group"
      data-testid={`rack-card-${rack.id}`}
    >
      {/* Rack Header */}
      <div className="flex items-center justify-between p-3 bg-[#0A0A0A] border-b border-white/[0.08]">
        <div className="flex items-center gap-3">
          <button 
            onClick={() => setIsCollapsed(!isCollapsed)}
            className="text-[#71717A] hover:text-white transition-colors"
          >
            {isCollapsed ? (
              <ChevronRight className="w-4 h-4" />
            ) : (
              <ChevronDown className="w-4 h-4" />
            )}
          </button>
          
          <Package className="w-5 h-5 text-[#34C759]" />
          
          {isEditing ? (
            <Input
              value={editName}
              onChange={(e) => setEditName(e.target.value)}
              onBlur={handleSaveName}
              onKeyDown={(e) => e.key === "Enter" && handleSaveName()}
              className="h-7 w-40 bg-[#18181B] border-white/20 text-white text-sm"
              autoFocus
            />
          ) : (
            <div>
              <h3 
                className="font-semibold text-white cursor-pointer hover:text-[#34C759] transition-colors"
                onClick={() => setIsEditing(true)}
              >
                {rack.name}
              </h3>
              {rack.location && (
                <p className="text-xs text-[#71717A] flex items-center gap-1">
                  <MapPin className="w-3 h-3" />
                  {rack.location}
                </p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Buttons row - separate line for better visibility */}
      <div className="flex items-center justify-between px-3 pb-2">
        <div className="flex items-center gap-2">
          <span className="text-xs text-[#71717A]">
            {filledSlots}/{maxCapacity} canales
          </span>
          <span className="text-[10px] text-[#52525B]">
            ({rack.slots_count}U)
          </span>
        </div>
        
        <div className="flex items-center gap-2">
          {/* Push to Network Button */}
          {filledSlots > 0 && (
            <>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onAnalyzeCoordination?.(rack.id);
                }}
                size="sm"
                variant="outline"
                className="h-7 border-[#FFCC00]/50 text-[#FFCC00] hover:bg-[#FFCC00]/10 text-xs px-3"
                title="Analizar frecuencias para detectar conflictos"
              >
                <Activity className="w-3 h-3 mr-1" />
                Analizar
              </Button>
              <Button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onPushToNetwork?.(rack.id);
                }}
                size="sm"
                className="h-7 bg-[#007AFF] hover:bg-[#007AFF]/90 text-white text-xs px-3"
                title="Enviar frecuencias a equipos en red"
              >
                <Zap className="w-3 h-3 mr-1" />
                Push
              </Button>
            </>
          )}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-7 w-7 p-0 text-[#71717A]">
                <MoreVertical className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#1A1A1A] border-white/10">
              <DropdownMenuItem 
                onClick={() => setIsEditing(true)}
                className="text-[#F4F4F5] focus:bg-white/10"
              >
                <Edit2 className="w-4 h-4 mr-2" />
                Renombrar
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={() => onDelete(rack.id)}
                className="text-[#FF3B30] focus:bg-[#FF3B30]/10"
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Eliminar Rack
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Rack Slots */}
      {!isCollapsed && (
        <div className="p-3 space-y-1">
          {Array.from({ length: rack.slots_count }, (_, i) => i + 1).map(slotNumber => (
            <SystemSlot
              key={slotNumber}
              slotNumber={slotNumber}
              systems={getSystemsForSlot(slotNumber)}
              onDrop={handleDrop}
              onRemove={(systemId) => onUnassignSystem(rack.id, systemId)}
              isDragging={getSystemsForSlot(slotNumber).some(s => s?.id === draggingSystemId)}
              onDragStart={onDragStart}
              onDragEnd={onDragEnd}
            />
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * UnassignedSystemsPanel - Panel showing systems not in any rack with bulk selection
 */
function UnassignedSystemsPanel({ 
  systems, 
  racks,
  onDragStart, 
  onDragEnd, 
  draggingSystemId,
  onBulkAssign 
}) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [selectedSystems, setSelectedSystems] = useState(new Set());
  const [showBulkAssignDialog, setShowBulkAssignDialog] = useState(false);
  const [targetRackId, setTargetRackId] = useState("");

  if (systems.length === 0) return null;

  const toggleSelection = (systemId) => {
    setSelectedSystems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(systemId)) {
        newSet.delete(systemId);
      } else {
        newSet.add(systemId);
      }
      return newSet;
    });
  };

  const toggleSelectAll = () => {
    if (selectedSystems.size === systems.length) {
      setSelectedSystems(new Set());
    } else {
      setSelectedSystems(new Set(systems.map(s => s.id)));
    }
  };

  const handleBulkAssign = async () => {
    if (!targetRackId || selectedSystems.size === 0) return;
    
    try {
      await onBulkAssign(Array.from(selectedSystems), targetRackId);
      setSelectedSystems(new Set());
      setShowBulkAssignDialog(false);
      setTargetRackId("");
    } catch (error) {
      toast.error("Error al asignar sistemas");
    }
  };

  return (
    <div className="bg-[#111111] border border-white/[0.08] rounded-sm overflow-hidden">
      <div 
        className="flex items-center justify-between p-3 bg-[#18181B] border-b border-white/[0.08]"
      >
        <div 
          className="flex items-center gap-2 cursor-pointer flex-1"
          onClick={() => setIsCollapsed(!isCollapsed)}
        >
          {isCollapsed ? (
            <ChevronRight className="w-4 h-4 text-[#71717A]" />
          ) : (
            <ChevronDown className="w-4 h-4 text-[#71717A]" />
          )}
          <Layers className="w-4 h-4 text-[#FFCC00]" />
          <span className="text-sm font-medium text-[#FFCC00]">Sin Asignar</span>
          <span className="text-xs text-[#71717A]">({systems.length})</span>
        </div>
        
        {/* Bulk selection controls */}
        {!isCollapsed && (
          <div className="flex items-center gap-2">
            {selectedSystems.size > 0 && (
              <>
                <span className="text-xs text-[#007AFF]">
                  {selectedSystems.size} seleccionados
                </span>
                <Button
                  onClick={() => setShowBulkAssignDialog(true)}
                  size="sm"
                  className="bg-[#34C759] text-black hover:bg-[#34C759]/90 text-xs h-7"
                >
                  <ArrowRight className="w-3 h-3 mr-1" />
                  Asignar a Rack
                </Button>
              </>
            )}
            <Button
              onClick={(e) => { e.stopPropagation(); toggleSelectAll(); }}
              variant="ghost"
              size="sm"
              className="text-xs text-[#71717A] hover:text-white h-7"
            >
              {selectedSystems.size === systems.length ? (
                <><CheckSquare className="w-3 h-3 mr-1" /> Deseleccionar</>
              ) : (
                <><Square className="w-3 h-3 mr-1" /> Seleccionar todos</>
              )}
            </Button>
          </div>
        )}
      </div>

      {!isCollapsed && (
        <div className="p-3 space-y-2 max-h-80 overflow-y-auto">
          {systems.map(system => {
            const isSelected = selectedSystems.has(system.id);
            return (
              <div
                key={system.id}
                draggable
                onDragStart={(e) => {
                  e.dataTransfer.setData("systemId", system.id);
                  onDragStart(system.id);
                }}
                onDragEnd={onDragEnd}
                className={`flex items-center gap-2 p-2 rounded border transition-colors cursor-grab active:cursor-grabbing
                  ${isSelected 
                    ? "border-[#007AFF]/50 bg-[#007AFF]/10" 
                    : "border-white/[0.05] bg-[#0A0A0A] hover:border-[#FFCC00]/30"
                  }
                  ${draggingSystemId === system.id ? "opacity-50" : ""}`}
                data-testid={`unassigned-system-${system.id}`}
              >
                {/* Checkbox - prevent drag when clicking */}
                <div onClick={(e) => e.stopPropagation()}>
                  <Checkbox
                    checked={isSelected}
                    onCheckedChange={() => toggleSelection(system.id)}
                    className="border-white/30"
                  />
                </div>
                
                {/* Drag handle indicator */}
                <GripVertical className="w-3 h-3 text-[#52525B] flex-shrink-0" />
                
                <Radio className="w-4 h-4 text-[#71717A] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-white truncate">{system.name || `Sistema ${system.channel}`}</p>
                  <p className="text-xs font-mono text-[#34C759]">
                    {parseFloat(system.frequency).toFixed(3)} MHz
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Bulk Assign Dialog */}
      <Dialog open={showBulkAssignDialog} onOpenChange={setShowBulkAssignDialog}>
        <DialogContent className="bg-[#111111] border-white/10 sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-[#F4F4F5] flex items-center gap-2">
              <Package className="w-5 h-5 text-[#34C759]" />
              Asignar {selectedSystems.size} Sistemas
            </DialogTitle>
          </DialogHeader>

          <div className="py-4">
            <Label className="text-[#A1A1AA]">Selecciona el Rack destino</Label>
            <Select value={targetRackId} onValueChange={setTargetRackId}>
              <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-2">
                <SelectValue placeholder="Seleccionar rack..." />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                {racks.map(rack => {
                  const usedSlots = Object.values(rack.slots || {}).filter(Boolean).length;
                  const availableSlots = rack.slots_count - usedSlots;
                  return (
                    <SelectItem 
                      key={rack.id} 
                      value={rack.id} 
                      className="text-white"
                      disabled={availableSlots === 0}
                    >
                      <div className="flex items-center justify-between w-full">
                        <span>{rack.name}</span>
                        <span className={`text-xs ml-2 ${
                          availableSlots > 0 ? 'text-[#34C759]' : 'text-[#FF3B30]'
                        }`}>
                          {availableSlots} slots libres
                        </span>
                      </div>
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
            
            <p className="text-xs text-[#71717A] mt-2">
              Los sistemas se asignarán a los slots disponibles en orden.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowBulkAssignDialog(false)}
              className="border-white/20 text-[#A1A1AA]"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleBulkAssign}
              disabled={!targetRackId}
              className="bg-[#34C759] text-black hover:bg-[#34C759]/90"
            >
              <ArrowRight className="w-4 h-4 mr-2" />
              Asignar {selectedSystems.size} Sistemas
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/**
 * CreateRackDialog - Dialog to create a new rack
 */
function CreateRackDialog({ open, onOpenChange, onSubmit, events, venues, selectedEventId, selectedVenueId }) {
  const [name, setName] = useState("");
  const [location, setLocation] = useState("");
  const [slotsCount, setSlotsCount] = useState(8);
  const [eventId, setEventId] = useState(selectedEventId || "");
  const [venueId, setVenueId] = useState(selectedVenueId || "");

  // Update eventId/venueId when props change
  useEffect(() => {
    setEventId(selectedEventId || "");
  }, [selectedEventId]);

  useEffect(() => {
    setVenueId(selectedVenueId || "");
  }, [selectedVenueId]);

  const handleSubmit = () => {
    if (!name.trim()) {
      toast.error("El nombre del rack es requerido");
      return;
    }
    onSubmit({ 
      name, 
      location, 
      slots_count: slotsCount, 
      event_id: eventId || null,
      venue_id: venueId || null
    });
    setName("");
    setLocation("");
    setSlotsCount(8);
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111111] border-white/10 sm:max-w-md">
        <DialogHeader>
          <DialogTitle className="text-[#F4F4F5] flex items-center gap-2">
            <Package className="w-5 h-5 text-[#34C759]" />
            Crear Nuevo Rack
          </DialogTitle>
        </DialogHeader>

        <div className="space-y-4 py-4">
          <div>
            <Label className="text-[#A1A1AA]">Nombre del Rack *</Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Ej: FOH Rack, Monitor Rack, Stage Left"
              className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              data-testid="rack-name-input"
            />
          </div>

          <div>
            <Label className="text-[#A1A1AA]">Ubicación (opcional)</Label>
            <Input
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ej: FOH, Escenario, Backstage"
              className="bg-[#0A0A0A] border-white/10 text-white mt-1"
              data-testid="rack-location-input"
            />
          </div>

          <div>
            <Label className="text-[#A1A1AA]">Número de Slots</Label>
            <div className="flex gap-2 mt-1">
              {[4, 6, 8, 12].map(num => (
                <Button
                  key={num}
                  onClick={() => setSlotsCount(num)}
                  variant={slotsCount === num ? "default" : "outline"}
                  size="sm"
                  className={slotsCount === num 
                    ? "bg-[#34C759] text-black" 
                    : "border-white/20 text-[#A1A1AA]"
                  }
                >
                  {num}
                </Button>
              ))}
            </div>
          </div>

          {/* Venue selector */}
          {venues && venues.length > 0 && (
            <div>
              <Label className="text-[#A1A1AA]">Asociar a Venue (opcional)</Label>
              <Select value={venueId || "none"} onValueChange={(v) => setVenueId(v === "none" ? "" : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                  <SelectValue placeholder="Sin venue" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="none" className="text-[#A1A1AA]">
                    Sin venue
                  </SelectItem>
                  {venues.map(venue => (
                    <SelectItem key={venue.id} value={venue.id} className="text-white">
                      <div className="flex items-center gap-2">
                        <Building className="w-3 h-3 text-[#FFCC00]" />
                        {venue.name} {venue.city && `- ${venue.city}`}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Event selector */}
          {events && events.length > 0 && (
            <div>
              <Label className="text-[#A1A1AA]">Asociar a Evento (opcional)</Label>
              <Select value={eventId || "none"} onValueChange={(v) => setEventId(v === "none" ? "" : v)}>
                <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white mt-1">
                  <SelectValue placeholder="Sin evento (rack global)" />
                </SelectTrigger>
                <SelectContent className="bg-[#1A1A1A] border-white/10">
                  <SelectItem value="none" className="text-[#A1A1AA]">
                    Sin evento (rack global)
                  </SelectItem>
                  {events.map(event => (
                    <SelectItem key={event.id} value={event.id} className="text-white">
                      <div className="flex items-center gap-2">
                        <Calendar className="w-3 h-3 text-[#007AFF]" />
                        {event.name}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-[#52525B] mt-1">
                Los racks asociados a un evento solo se muestran en ese evento
              </p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/20 text-[#A1A1AA]"
          >
            Cancelar
          </Button>
          <Button
            onClick={handleSubmit}
            className="bg-[#34C759] text-black hover:bg-[#34C759]/90"
            data-testid="create-rack-submit"
          >
            <Plus className="w-4 h-4 mr-2" />
            Crear Rack
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * CoordinationAnalysisDialog - Analyze rack frequencies for conflicts
 */
function CoordinationAnalysisDialog({ open, onOpenChange, rack, systems }) {
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);

  // Get systems in this rack
  const rackSystems = rack ? Object.values(rack.slots || {})
    .filter(Boolean)
    .map(sid => systems.find(s => s.id === sid))
    .filter(Boolean) : [];

  // Analyze when dialog opens
  useEffect(() => {
    if (open && rack && rackSystems.length > 0) {
      analyzeFrequencies();
    } else if (!open) {
      setResult(null);
    }
  }, [open, rack?.id]);

  const analyzeFrequencies = async () => {
    if (rackSystems.length === 0) return;

    setAnalyzing(true);
    try {
      const frequencies = rackSystems.map(sys => ({
        frequency_mhz: sys.frequency,
        device_id: sys.id,
        name: sys.name || `CH ${sys.channel}`,
        brand: sys.equipment_brand
      }));

      const res = await axios.post(
        `${API_BASE}/rf-network/coordination/analyze`,
        { frequencies },
        getAuthHeaders()
      );
      
      setResult(res.data);
    } catch (error) {
      toast.error("Error al analizar frecuencias");
      console.error(error);
    } finally {
      setAnalyzing(false);
    }
  };

  // Severity colors
  const getSeverityColor = (severity) => {
    switch(severity) {
      case 'critical': return '#FF3B30';
      case 'warning': return '#FFCC00';
      default: return '#71717A';
    }
  };

  const getSeverityIcon = (severity) => {
    switch(severity) {
      case 'critical': return <XCircle className="w-4 h-4" />;
      case 'warning': return <AlertTriangle className="w-4 h-4" />;
      default: return <AlertTriangle className="w-4 h-4" />;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="bg-[#111111] border-white/10 sm:max-w-lg"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-[#F4F4F5] flex items-center gap-2">
            <Activity className="w-5 h-5 text-[#FFCC00]" />
            Análisis de Coordinación RF
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Rack info */}
          {rack && (
            <div className="p-3 bg-[#0A0A0A] border border-white/[0.05] rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-[#34C759]" />
                <span className="text-white font-medium">{rack.name}</span>
                <Badge className="bg-[#34C759]/20 text-[#34C759] text-[10px]">
                  {rackSystems.length} sistemas
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-1">
                {rackSystems.map((sys) => (
                  <div key={sys.id} className="text-xs text-[#A1A1AA] flex items-center gap-1">
                    <Radio className="w-3 h-3 text-[#34C759]" />
                    <span className="truncate">{sys.name}</span>
                    <span className="font-mono text-[#34C759]">{sys.frequency?.toFixed(3)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Analysis Result */}
          {analyzing ? (
            <div className="flex flex-col items-center justify-center py-8">
              <Loader2 className="w-8 h-8 text-[#FFCC00] animate-spin mb-3" />
              <p className="text-sm text-[#A1A1AA]">Analizando frecuencias...</p>
            </div>
          ) : result ? (
            <div className="space-y-4">
              {/* Status badge */}
              <div className={`p-4 rounded-sm border ${
                result.is_clean 
                  ? 'bg-[#34C759]/10 border-[#34C759]/30' 
                  : 'bg-[#FF3B30]/10 border-[#FF3B30]/30'
              }`}>
                <div className="flex items-center gap-2">
                  {result.is_clean ? (
                    <>
                      <CheckCircle className="w-5 h-5 text-[#34C759]" />
                      <span className="text-[#34C759] font-medium">Sin Conflictos</span>
                    </>
                  ) : (
                    <>
                      <XCircle className="w-5 h-5 text-[#FF3B30]" />
                      <span className="text-[#FF3B30] font-medium">
                        {result.conflicts?.length || 0} Conflicto(s) Detectado(s)
                      </span>
                    </>
                  )}
                </div>
                {result.is_clean && (
                  <p className="text-xs text-[#A1A1AA] mt-1">
                    Todas las frecuencias tienen spacing adecuado y no hay intermodulación.
                  </p>
                )}
              </div>

              {/* Conflicts List */}
              {result.conflicts?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Conflictos detectados:</Label>
                  <ScrollArea className="max-h-48">
                    <div className="space-y-2">
                      {result.conflicts.map((conflict, idx) => (
                        <div 
                          key={idx}
                          className="p-3 rounded-sm border"
                          style={{ 
                            backgroundColor: `${getSeverityColor(conflict.severity)}10`,
                            borderColor: `${getSeverityColor(conflict.severity)}30`
                          }}
                        >
                          <div className="flex items-start gap-2">
                            <div style={{ color: getSeverityColor(conflict.severity) }}>
                              {getSeverityIcon(conflict.severity)}
                            </div>
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Badge 
                                  className="text-[9px]"
                                  style={{ 
                                    backgroundColor: `${getSeverityColor(conflict.severity)}20`,
                                    color: getSeverityColor(conflict.severity)
                                  }}
                                >
                                  {conflict.type === 'spacing' ? 'SPACING' : 
                                   conflict.type === 'intermod' ? 'IMD' :
                                   conflict.type === 'duplicate' ? 'DUPLICADO' : conflict.type.toUpperCase()}
                                </Badge>
                                <span className="text-[10px] text-[#52525B]">
                                  {conflict.severity === 'critical' ? 'CRÍTICO' : 'ADVERTENCIA'}
                                </span>
                              </div>
                              <p className="text-xs text-[#A1A1AA] mt-1">{conflict.message}</p>
                              {conflict.frequency_a_mhz && conflict.frequency_b_mhz && (
                                <p className="text-xs font-mono text-[#71717A] mt-1">
                                  {conflict.frequency_a_mhz?.toFixed(3)} MHz ↔ {conflict.frequency_b_mhz?.toFixed(3)} MHz
                                </p>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </ScrollArea>
                </div>
              )}

              {/* Suggestions */}
              {result.suggestions?.length > 0 && (
                <div className="space-y-2">
                  <Label className="text-[#A1A1AA]">Frecuencias alternativas sugeridas:</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {result.suggestions.map((suggestion, idx) => (
                      <div 
                        key={idx}
                        className="p-2 bg-[#34C759]/10 border border-[#34C759]/30 rounded-sm"
                      >
                        <span className="text-xs font-mono text-[#34C759]">
                          {suggestion.frequency_mhz?.toFixed(3)} MHz
                        </span>
                        {suggestion.name && (
                          <p className="text-[10px] text-[#71717A] truncate">{suggestion.name}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="text-center py-8 text-[#71717A]">
              <Activity className="w-8 h-8 mx-auto mb-2 text-[#52525B]" />
              <p className="text-sm">No hay datos de análisis</p>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/20 text-[#A1A1AA]"
          >
            Cerrar
          </Button>
          <Button
            onClick={analyzeFrequencies}
            disabled={analyzing || rackSystems.length === 0}
            className="bg-[#FFCC00] text-black hover:bg-[#FFCC00]/90"
          >
            {analyzing ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Activity className="w-4 h-4 mr-2" />
            )}
            Re-Analizar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * PushToNetworkDialog - Dialog to push rack frequencies to network devices
 */
function PushToNetworkDialog({ open, onOpenChange, rack, systems, networkDevices, loadingDevices }) {
  const [selectedDevices, setSelectedDevices] = useState([]);
  const [pushing, setPushing] = useState(false);
  const [pingingAll, setPingingAll] = useState(false);
  const [deviceStatuses, setDeviceStatuses] = useState({});

  // Get systems in this rack
  const rackSystems = rack ? Object.values(rack.slots || {})
    .filter(Boolean)
    .map(sid => systems.find(s => s.id === sid))
    .filter(Boolean) : [];

  // Initialize device statuses from networkDevices
  useEffect(() => {
    if (networkDevices.length > 0) {
      const statuses = {};
      networkDevices.forEach(d => {
        statuses[d.id] = d.status || 'unknown';
      });
      setDeviceStatuses(statuses);
    }
  }, [networkDevices]);

  const toggleDevice = (deviceId) => {
    setSelectedDevices(prev => 
      prev.includes(deviceId) 
        ? prev.filter(id => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  // Ping all devices to check connectivity
  const pingAllDevices = async () => {
    if (networkDevices.length === 0) return;
    
    setPingingAll(true);
    try {
      const res = await axios.post(
        `${API_BASE}/network-devices/ping-all`,
        {},
        getAuthHeaders()
      );
      
      // Update statuses
      const newStatuses = {};
      res.data.devices.forEach(d => {
        newStatuses[d.device_id] = d.status;
      });
      setDeviceStatuses(newStatuses);
      
      toast.success(`✓ ${res.data.online_count} en línea, ${res.data.offline_count} sin conexión`);
    } catch (error) {
      toast.error("Error al verificar dispositivos");
    } finally {
      setPingingAll(false);
    }
  };

  const handlePush = async () => {
    if (selectedDevices.length === 0) {
      toast.error("Selecciona al menos un dispositivo");
      return;
    }

    setPushing(true);
    try {
      const res = await axios.post(
        `${API_BASE}/network-devices/push-from-rack/${rack.id}`,
        selectedDevices,
        getAuthHeaders()
      );
      toast.success(res.data.message || "Frecuencias enviadas");
      onOpenChange(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar frecuencias");
    } finally {
      setPushing(false);
    }
  };

  // Brand colors
  const getBrandColor = (brand) => {
    switch(brand) {
      case 'sennheiser': return '#00A0E3';
      case 'shure': return '#E31837';
      default: return '#71717A';
    }
  };

  // Status indicator
  const getStatusIndicator = (deviceId) => {
    const status = deviceStatuses[deviceId] || 'unknown';
    switch(status) {
      case 'online':
        return <Wifi className="w-4 h-4 text-[#34C759]" title="En línea" />;
      case 'offline':
        return <WifiOff className="w-4 h-4 text-[#FF3B30]" title="Sin conexión" />;
      default:
        return <Wifi className="w-4 h-4 text-[#71717A]" title="Estado desconocido" />;
    }
  };

  const getStatusBadge = (deviceId) => {
    const status = deviceStatuses[deviceId] || 'unknown';
    switch(status) {
      case 'online':
        return <Badge className="bg-[#34C759]/20 text-[#34C759] text-[9px] px-1">Online</Badge>;
      case 'offline':
        return <Badge className="bg-[#FF3B30]/20 text-[#FF3B30] text-[9px] px-1">Offline</Badge>;
      default:
        return null;
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent 
        className="bg-[#111111] border-white/10 sm:max-w-lg"
        onOpenAutoFocus={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <DialogTitle className="text-[#F4F4F5] flex items-center gap-2">
            <Zap className="w-5 h-5 text-[#007AFF]" />
            Enviar a Equipos en Red
          </DialogTitle>
        </DialogHeader>

        <div className="py-4 space-y-4">
          {/* Rack info */}
          {rack && (
            <div className="p-3 bg-[#0A0A0A] border border-white/[0.05] rounded-sm">
              <div className="flex items-center gap-2 mb-2">
                <Package className="w-4 h-4 text-[#34C759]" />
                <span className="text-white font-medium">{rack.name}</span>
                <Badge className="bg-[#34C759]/20 text-[#34C759] text-[10px]">
                  {rackSystems.length} sistemas
                </Badge>
              </div>
              <div className="grid grid-cols-2 gap-1">
                {rackSystems.slice(0, 4).map((sys, idx) => (
                  <div key={sys.id} className="text-xs text-[#A1A1AA] flex items-center gap-1">
                    <Radio className="w-3 h-3 text-[#34C759]" />
                    <span className="truncate">{sys.name}</span>
                    <span className="font-mono text-[#34C759]">{sys.frequency?.toFixed(3)}</span>
                  </div>
                ))}
                {rackSystems.length > 4 && (
                  <div className="text-xs text-[#52525B]">+{rackSystems.length - 4} más</div>
                )}
              </div>
            </div>
          )}

          {/* Network devices selection */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="text-[#A1A1AA]">Selecciona dispositivos destino:</Label>
              {networkDevices.length > 0 && (
                <Button
                  onClick={pingAllDevices}
                  variant="ghost"
                  size="sm"
                  disabled={pingingAll}
                  className="h-6 text-xs text-[#007AFF] hover:text-[#007AFF] hover:bg-[#007AFF]/10 px-2"
                >
                  {pingingAll ? (
                    <Loader2 className="w-3 h-3 animate-spin mr-1" />
                  ) : (
                    <RefreshCw className="w-3 h-3 mr-1" />
                  )}
                  Verificar conexión
                </Button>
              )}
            </div>
            
            {loadingDevices ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 text-[#007AFF] animate-spin" />
              </div>
            ) : networkDevices.length === 0 ? (
              <div className="text-center py-6 bg-[#0A0A0A] border border-white/[0.05] rounded-sm">
                <Server className="w-8 h-8 text-[#52525B] mx-auto mb-2" />
                <p className="text-sm text-[#71717A]">No hay dispositivos de red</p>
                <p className="text-xs text-[#52525B]">Agrega dispositivos en Hardware RF → Red</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-48 overflow-y-auto">
                {networkDevices.map(device => {
                  const status = deviceStatuses[device.id] || 'unknown';
                  const isOffline = status === 'offline';
                  
                  return (
                    <div
                      key={device.id}
                      onClick={() => toggleDevice(device.id)}
                      className={`p-3 rounded-sm border cursor-pointer transition-all ${
                        selectedDevices.includes(device.id)
                          ? 'border-[#007AFF] bg-[#007AFF]/10'
                          : isOffline
                            ? 'border-[#FF3B30]/30 bg-[#FF3B30]/5 hover:border-[#FF3B30]/50'
                            : 'border-white/[0.05] bg-[#0A0A0A] hover:border-white/[0.1]'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        {/* Status indicator */}
                        <div className={`w-8 h-8 rounded flex items-center justify-center flex-shrink-0 ${
                          status === 'online' ? 'bg-[#34C759]/20' :
                          status === 'offline' ? 'bg-[#FF3B30]/20' :
                          'bg-[#27272A]'
                        }`}>
                          {pingingAll ? (
                            <Loader2 className="w-4 h-4 text-[#007AFF] animate-spin" />
                          ) : (
                            getStatusIndicator(device.id)
                          )}
                        </div>
                        
                        {/* Checkbox */}
                        <div className={`w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 ${
                          selectedDevices.includes(device.id)
                            ? 'bg-[#007AFF] border-[#007AFF]'
                            : 'border-[#52525B]'
                        }`}>
                          {selectedDevices.includes(device.id) && (
                            <CheckCircle className="w-3 h-3 text-white" />
                          )}
                        </div>
                        
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="text-sm text-white truncate">{device.name}</p>
                            {getStatusBadge(device.id)}
                          </div>
                          <p className="text-xs">
                            <span style={{ color: getBrandColor(device.brand) }}>
                              {device.brand === 'sennheiser' ? 'Sennheiser' : device.brand === 'shure' ? 'Shure' : device.brand}
                            </span>
                            {" "}{device.model} • {device.channels} CH
                          </p>
                        </div>
                        <span className="text-xs font-mono text-[#52525B]">{device.ip_address}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          {/* Warning */}
          {selectedDevices.length > 0 && (
            <div className="p-3 bg-[#FFCC00]/10 border border-[#FFCC00]/30 rounded-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 text-[#FFCC00] mt-0.5 flex-shrink-0" />
                <div className="text-xs text-[#FFCC00]">
                  <p className="font-medium">Atención</p>
                  <p className="text-[#A1A1AA]">
                    Esto cambiará las frecuencias en {selectedDevices.length} dispositivo(s). 
                    Verifica que los transmisores estén apagados.
                  </p>
                </div>
              </div>
            </div>
          )}
          
          {/* Offline warning */}
          {selectedDevices.some(id => deviceStatuses[id] === 'offline') && (
            <div className="p-3 bg-[#FF3B30]/10 border border-[#FF3B30]/30 rounded-sm">
              <div className="flex items-start gap-2">
                <WifiOff className="w-4 h-4 text-[#FF3B30] mt-0.5 flex-shrink-0" />
                <div className="text-xs">
                  <p className="font-medium text-[#FF3B30]">Dispositivo sin conexión</p>
                  <p className="text-[#A1A1AA]">
                    Uno o más dispositivos seleccionados no responden. El envío podría fallar.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/20 text-[#A1A1AA]"
          >
            Cancelar
          </Button>
          <Button
            onClick={handlePush}
            disabled={pushing || selectedDevices.length === 0 || networkDevices.length === 0}
            className="bg-[#007AFF] text-white hover:bg-[#007AFF]/90"
          >
            {pushing ? (
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
            ) : (
              <Zap className="w-4 h-4 mr-2" />
            )}
            Enviar Frecuencias
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * RackView - Main rack management view
 */
export function RackView({ systems }) {
  const [racks, setRacks] = useState([]);
  const [unassignedSystems, setUnassignedSystems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [draggingSystemId, setDraggingSystemId] = useState(null);
  const [events, setEvents] = useState([]);
  const [venues, setVenues] = useState([]);
  const [selectedEventId, setSelectedEventId] = useState("");
  const [selectedVenueId, setSelectedVenueId] = useState("");
  const [showPushDialog, setShowPushDialog] = useState(false);
  const [pushRackId, setPushRackId] = useState(null);
  const [networkDevices, setNetworkDevices] = useState([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [showAnalyzeDialog, setShowAnalyzeDialog] = useState(false);
  const [analyzeRackId, setAnalyzeRackId] = useState(null);

  // Handle analyze coordination
  const handleAnalyzeCoordination = (rackId) => {
    setAnalyzeRackId(rackId);
    setShowAnalyzeDialog(true);
  };

  // Handle push to network
  const handlePushToNetwork = async (rackId) => {
    setPushRackId(rackId);
    setShowPushDialog(true);
    setLoadingDevices(true);
    try {
      const res = await axios.get(`${API_BASE}/network-devices`, getAuthHeaders());
      setNetworkDevices(res.data.devices || []);
    } catch (error) {
      console.error("Error fetching network devices:", error);
      toast.error("Error al cargar dispositivos de red");
    } finally {
      setLoadingDevices(false);
    }
  };

  // Fetch events and venues
  useEffect(() => {
    const fetchEventsAndVenues = async () => {
      try {
        const [eventsRes, venuesRes] = await Promise.all([
          axios.get(`${API_BASE}/events`, getAuthHeaders()),
          axios.get(`${API_BASE}/venues`, getAuthHeaders())
        ]);
        // API returns array directly or {events: [...]}
        const eventsData = Array.isArray(eventsRes.data) ? eventsRes.data : (eventsRes.data.events || []);
        setEvents(eventsData);
        // Venues API returns { venues: [...] }
        setVenues(venuesRes.data.venues || []);
      } catch (error) {
        console.error("Error fetching events/venues:", error);
      }
    };
    fetchEventsAndVenues();
  }, []);

  // Fetch racks and unassigned systems
  const fetchData = async () => {
    try {
      let rackUrl = `${API_BASE}/racks`;
      const params = [];
      if (selectedEventId) params.push(`event_id=${selectedEventId}`);
      if (selectedVenueId) params.push(`venue_id=${selectedVenueId}`);
      if (params.length > 0) rackUrl += `?${params.join("&")}`;
        
      const [racksRes, unassignedRes] = await Promise.all([
        axios.get(rackUrl, getAuthHeaders()),
        axios.get(`${API_BASE}/racks/unassigned/systems`, getAuthHeaders())
      ]);
      
      setRacks(racksRes.data.racks || []);
      setUnassignedSystems(unassignedRes.data.systems || []);
    } catch (error) {
      console.error("Error fetching racks:", error);
      toast.error("Error al cargar los racks");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedEventId, selectedVenueId]);

  // Refresh when systems prop changes
  useEffect(() => {
    if (systems?.length > 0) {
      fetchData();
    }
  }, [systems?.length]);

  const handleCreateRack = async (rackData) => {
    try {
      const response = await axios.post(`${API_BASE}/racks`, rackData, getAuthHeaders());
      setRacks(prev => [...prev, response.data.rack]);
      toast.success(`Rack "${rackData.name}" creado`);
    } catch (error) {
      toast.error("Error al crear el rack");
    }
  };

  const handleUpdateRack = async (rackId, updates) => {
    try {
      const response = await axios.put(`${API_BASE}/racks/${rackId}`, updates, getAuthHeaders());
      setRacks(prev => prev.map(r => r.id === rackId ? response.data.rack : r));
      toast.success("Rack actualizado");
    } catch (error) {
      toast.error("Error al actualizar el rack");
    }
  };

  const handleDeleteRack = async (rackId) => {
    if (!window.confirm("¿Eliminar este rack? Los sistemas no serán eliminados.")) return;
    
    try {
      await axios.delete(`${API_BASE}/racks/${rackId}`, getAuthHeaders());
      setRacks(prev => prev.filter(r => r.id !== rackId));
      fetchData(); // Refresh to update unassigned
      toast.success("Rack eliminado");
    } catch (error) {
      toast.error("Error al eliminar el rack");
    }
  };

  const handleAssignSystem = async (rackId, systemId, slotPosition) => {
    try {
      await axios.post(
        `${API_BASE}/racks/${rackId}/assign/${systemId}?slot_position=${slotPosition}`,
        {},
        getAuthHeaders()
      );
      fetchData();
      toast.success("Sistema asignado al rack");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al asignar sistema");
    }
  };

  const handleUnassignSystem = async (rackId, systemId) => {
    try {
      await axios.post(
        `${API_BASE}/racks/${rackId}/unassign/${systemId}`,
        {},
        getAuthHeaders()
      );
      fetchData();
    } catch (error) {
      toast.error("Error al remover sistema del rack");
    }
  };

  // Bulk assign handler
  const handleBulkAssign = async (systemIds, rackId) => {
    try {
      const response = await axios.post(
        `${API_BASE}/racks/bulk-assign`,
        { system_ids: systemIds, rack_id: rackId },
        getAuthHeaders()
      );
      toast.success(response.data.message || `${systemIds.length} sistemas asignados`);
      fetchData(); // Refresh data
    } catch (error) {
      const message = error.response?.data?.detail || "Error al asignar sistemas";
      toast.error(message);
      throw error;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <div className="w-8 h-8 border-2 border-[#34C759]/30 border-t-[#34C759] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-[#F4F4F5]">Vista por Racks</h2>
          <p className="text-sm text-[#71717A]">
            Organiza tus sistemas como racks físicos. Arrastra para reorganizar.
          </p>
        </div>
        <div className="flex items-center gap-3">
          {/* Venue Filter */}
          {venues.length > 0 && (
            <Select value={selectedVenueId || "all"} onValueChange={(v) => setSelectedVenueId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48 bg-[#0A0A0A] border-white/10 text-white">
                <Building className="w-4 h-4 mr-2 text-[#FFCC00]" />
                <SelectValue placeholder="Todas los venues" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all" className="text-[#A1A1AA]">
                  Todos los venues
                </SelectItem>
                {venues.map(venue => (
                  <SelectItem key={venue.id} value={venue.id} className="text-white">
                    {venue.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          {/* Event Filter */}
          {events.length > 0 && (
            <Select value={selectedEventId || "all"} onValueChange={(v) => setSelectedEventId(v === "all" ? "" : v)}>
              <SelectTrigger className="w-48 bg-[#0A0A0A] border-white/10 text-white">
                <Calendar className="w-4 h-4 mr-2 text-[#007AFF]" />
                <SelectValue placeholder="Todos los racks" />
              </SelectTrigger>
              <SelectContent className="bg-[#1A1A1A] border-white/10">
                <SelectItem value="all" className="text-[#A1A1AA]">
                  Todos los eventos
                </SelectItem>
                {events.map(event => (
                  <SelectItem key={event.id} value={event.id} className="text-white">
                    {event.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-[#34C759] text-black hover:bg-[#34C759]/90"
            data-testid="create-rack-button"
          >
            <Plus className="w-4 h-4 mr-2" />
            Crear Rack
          </Button>
        </div>
      </div>

      {/* Selected Filters Badge */}
      {(selectedEventId || selectedVenueId) && (
        <div className="flex flex-wrap items-center gap-2 p-3 bg-[#007AFF]/10 border border-[#007AFF]/30 rounded-sm">
          {selectedVenueId && (
            <div className="flex items-center gap-2">
              <Building className="w-4 h-4 text-[#FFCC00]" />
              <span className="text-sm text-[#FFCC00]">
                Venue: <strong>{venues.find(v => v.id === selectedVenueId)?.name}</strong>
              </span>
            </div>
          )}
          {selectedEventId && (
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#007AFF]" />
              <span className="text-sm text-[#007AFF]">
                Evento: <strong>{events.find(e => e.id === selectedEventId)?.name}</strong>
              </span>
            </div>
          )}
          <Button
            onClick={() => { setSelectedEventId(""); setSelectedVenueId(""); }}
            variant="ghost"
            size="sm"
            className="ml-auto text-[#007AFF] hover:text-white text-xs"
          >
            Limpiar filtros
          </Button>
        </div>
      )}

      {/* Unassigned Systems */}
      <UnassignedSystemsPanel
        systems={unassignedSystems}
        racks={racks}
        onDragStart={setDraggingSystemId}
        onDragEnd={() => setDraggingSystemId(null)}
        draggingSystemId={draggingSystemId}
        onBulkAssign={handleBulkAssign}
      />

      {/* Racks Grid */}
      {racks.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {racks.map(rack => (
            <RackCard
              key={rack.id}
              rack={rack}
              systems={systems}
              onUpdate={handleUpdateRack}
              onDelete={handleDeleteRack}
              onAssignSystem={handleAssignSystem}
              onUnassignSystem={handleUnassignSystem}
              draggingSystemId={draggingSystemId}
              onDragStart={setDraggingSystemId}
              onDragEnd={() => setDraggingSystemId(null)}
              onPushToNetwork={handlePushToNetwork}
              onAnalyzeCoordination={handleAnalyzeCoordination}
            />
          ))}
        </div>
      ) : (
        <div className="text-center py-16 bg-[#111111] border border-white/[0.08] rounded-sm">
          <Package className="w-12 h-12 mx-auto mb-4 text-[#71717A] opacity-30" />
          <h3 className="text-[#F4F4F5] font-medium mb-2">No hay racks creados</h3>
          <p className="text-[#71717A] text-sm mb-4">
            Crea tu primer rack para organizar tus sistemas inalámbricos
          </p>
          <Button
            onClick={() => setShowCreateDialog(true)}
            className="bg-[#34C759] text-black hover:bg-[#34C759]/90"
          >
            <Plus className="w-4 h-4 mr-2" />
            Crear Primer Rack
          </Button>
        </div>
      )}

      {/* Create Rack Dialog */}
      <CreateRackDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        onSubmit={handleCreateRack}
        events={events}
        venues={venues}
        selectedEventId={selectedEventId}
        selectedVenueId={selectedVenueId}
      />

      {/* Push to Network Dialog */}
      <PushToNetworkDialog
        open={showPushDialog}
        onOpenChange={setShowPushDialog}
        rack={racks.find(r => r.id === pushRackId)}
        systems={systems}
        networkDevices={networkDevices}
        loadingDevices={loadingDevices}
      />

      {/* Coordination Analysis Dialog */}
      <CoordinationAnalysisDialog
        open={showAnalyzeDialog}
        onOpenChange={setShowAnalyzeDialog}
        rack={racks.find(r => r.id === analyzeRackId)}
        systems={systems}
      />
    </div>
  );
}

export default RackView;
